import React, { useState, useEffect } from 'react';
import Footerdiv from "../layout/footer.js";
import Headerdiv from "../layout/header.js";
import {
    Layout,
    Typography,
    Descriptions,
    Space,
    Breadcrumb,
    Spin,
    Tag,
    Divider,
    Table,
    theme,
} from 'antd';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MAINAPIURL, APPBACKGROUND_COLOR } from '../appsettings.js';
import { AlertError } from '../usableclasses.js';

const { Title } = Typography;
const { Content } = Layout;

export const StockTransferView = () => {
    const { transferId } = useParams();
    const ApiURL = MAINAPIURL;
    const TOKEN = localStorage.getItem("AUTHCODE");

    const { token: { colorBgContainer, borderRadiusLG }, } = theme.useToken();

    const [stockTransferHeader, setStockTransferHeader] = useState(null);
    const [transferItems, setTransferItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [itemsLoading, setItemsLoading] = useState(true);

    // Format date helper function
    const formatDate = (dateString) => {
        if (!dateString) return '';
        // Assuming dateString is in "MM/DD/YYYY HH:mm:ss" format or similar
        // If it's a standard ISO string, new Date() will parse it correctly.
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) { // Check if date is invalid
                // Try to parse "MM/DD/YYYY ..." format if needed
                const parts = dateString.split(' ')[0].split('/');
                if (parts.length === 3) {
                    // new Date(year, monthIndex, day)
                    const formattedDate = new Date(parts[2], parts[0] - 1, parts[1]);
                    return formattedDate.toLocaleDateString();
                }
                return dateString; // Return original if parsing fails
            }
            return date.toLocaleDateString();
        } catch (e) {
            return dateString;
        }
    };

    useEffect(() => {
        const fetchStockTransferHeader = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${ApiURL}/stocktransfer/transferheaderdetails/${transferId}`, {
                    headers: { SystemAccessToken: TOKEN }
                });
                if (response.data) {
                    setStockTransferHeader(response.data);
                } else {
                    AlertError('Failed to load stock transfer header data');
                }
            } catch (error) {
                AlertError('Error loading stock transfer header details');
            } finally {
                setLoading(false);
            }
        };

        const fetchStockTransferItems = async () => {
            setItemsLoading(true);
            try {
                const response = await axios.get(`${ApiURL}/stocktransfer/transferitemsdetails/${transferId}`, {
                    headers: { SystemAccessToken: TOKEN }
                });
                if (response.data) {
                    setTransferItems(Array.isArray(response.data) ? response.data : []);
                } else {
                    AlertError('Failed to load stock transfer items');
                }
            } catch (error) {
                AlertError('Error loading stock transfer items');
            } finally {
                setItemsLoading(false);
            }
        };

        if (transferId) {
            fetchStockTransferHeader();
            fetchStockTransferItems();
        }
    }, [transferId, ApiURL, TOKEN]);

    const getStatusBadge = (status) => {
        switch ((status || '').toUpperCase()) {
            case 'DRAFT':
                return <Tag color="blue">Draft</Tag>;
            case 'COMPLETED':
                return <Tag color="green">Completed</Tag>;
            case 'CANCELED':
                return <Tag color="red">Canceled</Tag>;
            default:
                return <Tag color="default">{status}</Tag>;
        }
    };

    const itemColumns = [
        {
            title: <strong>Code</strong>,
            dataIndex: 'productSku',
            key: 'productSku',
        },
        {
            title: <strong>Item Name</strong>,
            dataIndex: 'productName',
            key: 'productName',
        },
        {
            title: <strong>Size</strong>,
            dataIndex: 'productSize',
            key: 'productSize',
        },
        {
            title: <strong>UOM</strong>,
            dataIndex: 'productUOM',
            key: 'productUOM',
        },
        {
            title: <strong>Transfer Qty</strong>,
            dataIndex: 'transferQty',
            key: 'transferQty',
            align: 'right',
        },
    ];

    return (
        <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
            <Headerdiv />
            <Content style={{ padding: '10px' }}>

                <Breadcrumb style={{ margin: "16px 0" }}>
                    <Breadcrumb.Item> <Link to={"/stocks/transfer"}>Stock Transfer</Link></Breadcrumb.Item>
                    <Breadcrumb.Item>View</Breadcrumb.Item> 
                    <Breadcrumb.Item>{transferId}</Breadcrumb.Item>
                </Breadcrumb>

                <div style={{ padding: 20, minHeight: '65vh', background: colorBgContainer, borderRadius: borderRadiusLG }}>
                    <Space direction="vertical" style={{ width: "100%", padding: "15px" }}>

                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '50px' }}>
                                <Spin size="large" />
                                <div style={{ marginTop: 16 }}>Loading transfer details...</div>
                            </div>
                        ) : stockTransferHeader ? (
                            <>
                                <div style={{ overflowX: 'auto' }}>
                                    <Descriptions
                                        bordered
                                        column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}
                                        title="Basic Information"
                                        layout="vertical"
                                        size="small"
                                    >
                                        <Descriptions.Item label={<strong>Transfer Reference</strong>}>{stockTransferHeader.transReferance || 'N/A'}</Descriptions.Item>
                                        <Descriptions.Item label={<strong>Create Date</strong>}>{formatDate(stockTransferHeader.createdDate)}</Descriptions.Item>
                                        <Descriptions.Item label={<strong>Month</strong>}>{stockTransferHeader.sysMonth}</Descriptions.Item>
                                        <Descriptions.Item label={<strong>Year</strong>}>{stockTransferHeader.sysYear}</Descriptions.Item>
                                        <Descriptions.Item label={<strong>Financial Year</strong>}>{stockTransferHeader.finYear}</Descriptions.Item>
                                        <Descriptions.Item label={<strong>Status</strong>}>{getStatusBadge(stockTransferHeader.currentStatus)}</Descriptions.Item>
                                        <Descriptions.Item label={<strong>Total Products</strong>}>{stockTransferHeader.totalProducts}</Descriptions.Item>
                                        <Descriptions.Item label={<strong>Total Quantity</strong>}>{stockTransferHeader.totalQty}</Descriptions.Item>
                                        <Descriptions.Item label={<strong>From Stock</strong>}>{stockTransferHeader.fromStockName}</Descriptions.Item>
                                        <Descriptions.Item label={<strong>To Stock</strong>}>{stockTransferHeader.toStockName}</Descriptions.Item>
                                        <Descriptions.Item label={<strong>Created By</strong>}>{stockTransferHeader.createdUsername}</Descriptions.Item>
                                        <Descriptions.Item label={<strong>Created On</strong>}>{formatDate(stockTransferHeader.createdDatetime)}</Descriptions.Item>
                                        {stockTransferHeader.completedDate && <Descriptions.Item label={<strong>Completed Date</strong>}>{formatDate(stockTransferHeader.completedDate)}</Descriptions.Item>}
                                    </Descriptions>
                                </div>

                                <Divider />

                                <Title level={5}>Items</Title>

                                {itemsLoading ? (
                                    <div style={{ textAlign: 'center', padding: '20px' }}>
                                        <Spin />
                                        <div>Loading items...</div>
                                    </div>
                                ) : transferItems && transferItems.length > 0 ? (
                                    <div style={{ overflowX: 'auto' }}>
                                        <Table
                                            dataSource={transferItems}
                                            columns={itemColumns}
                                            rowKey="transItemId"
                                            pagination={false}
                                            scroll={{ x: 'max-content' }}
                                            summary={() => (
                                                <Table.Summary.Row style={{ backgroundColor: "#f0f2f5", fontWeight: "bold" }}>
                                                    <Table.Summary.Cell index={0} colSpan={4}><b>Total</b></Table.Summary.Cell>
                                                    <Table.Summary.Cell index={1} align="right">
                                                        <b>{stockTransferHeader.totalQty || transferItems.reduce((sum, item) => sum + parseInt(item.transferQty || 0), 0)}</b>
                                                    </Table.Summary.Cell>
                                                </Table.Summary.Row>
                                            )}
                                        />
                                    </div>
                                ) : (
                                    <p>No items found for this stock transfer.</p>
                                )}
                            </>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '50px' }}>
                                <p>Stock transfer details not found.</p>
                            </div>
                        )}

                    </Space>
                </div>
            </Content>
            <Footerdiv />
        </Layout>
    )
}
